import { getStopById } from 'Global/JourneyElements/Utils/getStopById';
import { action, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { store } from 'Models/Store';
import * as React from 'react';
import { useEffect } from 'react';
import { Checkbox } from 'semantic-ui-react';
import { formatCurrency } from 'Util/_HumanWritten/CurrencyUtils';
import { getSelectedBookingsCutoffInfo } from 'Views/Components/_HumanWritten/Bookings/BookingDetailedView/Modals/CancelBooking/CustomerModalContent';
import {
	BookingCancellationRefundDto,
	computeSelectedTotalForMethod,
	getMethodBookingCutoff,
	getMethodRefundAmount,
	RefundModel,
} from 'Views/Components/_HumanWritten/Bookings/BookingDetailedView/Modals/CancelBooking/NewCancelBookingModal';
import { Button } from 'Views/Components/Button/Button';
import If from 'Views/Components/If/If';

export const BookingRefundList = observer(
	({
		bookings,
		refundModel,
		onRefundChange,
	}: {
		bookings: BookingCancellationRefundDto[];
		refundModel: RefundModel | RefundModel;
		onRefundChange: (bookingId: string, amount: number) => void;
	}) => {
		// Function to handle checkbox selection/deselection
		const handleBookingSelection = action((bookingId: string, isSelected: boolean) => {
			if (isSelected) {
				// Add booking to selection if not already selected
				if (!refundModel.selectedBookings.includes(bookingId)) {
					refundModel.selectedBookings.push(bookingId);
				}
			} else {
				// Remove booking from selection
				const index = refundModel.selectedBookings.indexOf(bookingId);
				if (index > -1) {
					refundModel.selectedBookings.splice(index, 1);
				}
			}

			// Update the total refund amount to be the sum of selected bookings' recommended amounts
			// for the currently-selected refund method.
			runInAction(() => {
				refundModel.refundAmount = computeSelectedTotalForMethod(
					bookings,
					refundModel.selectedBookings,
					refundModel.forceCreditRefund,
				);
			});
		});

		// Initialize refund amount on component mount if not already set
		useEffect(() => {
			if (refundModel.selectedBookings.length > 0) {
				runInAction(() => {
					refundModel.refundAmount = computeSelectedTotalForMethod(
						bookings,
						refundModel.selectedBookings,
						refundModel.forceCreditRefund,
					);
				});
			}
		}, []); // Only run once on mount

		return (
			<div>
				<p className="input-label">Select bookings</p>
				<div className="refund-bookings__list">
					{bookings.map(booking => {
						const refundAmount = getMethodRefundAmount(booking, refundModel.forceCreditRefund);
						const matchedCutoff = getMethodBookingCutoff(booking, refundModel.forceCreditRefund);
						const isSelected = refundModel.selectedBookings?.includes(booking.id);
						const { cutoffRules } = getSelectedBookingsCutoffInfo([booking], refundModel.forceCreditRefund);
						const [expanded, setExpanded] = React.useState(false);

						return (
							<div
								key={booking.id}
								className="booking-refund-item"
								data-test-id={`cancel-booking-refund-item-${booking.mainBooking ? 'main' : 'linked'}`}
							>
								<div className="top-content">
									<Checkbox
										label={`${getStopById(booking.startStopId)?.location.name} to ${getStopById(booking.endStopId)?.location.name}`}
										checked={isSelected}
										onChange={(e, data) =>
											handleBookingSelection(booking.id, data.checked || false)
										}
										disabled={booking.mainBooking}
									/>
									<div className="refund-amount-display">
										{booking.maximumRefund !== refundAmount && (
											<Button
												onClick={() => setExpanded(!expanded)}
												icon={{
													icon: expanded ? 'chevron-down' : 'chevron-up',
													iconPos: 'icon-right',
												}}
												buttonProps={{ 'data-test-id': 'cancel-booking-expand-chevron' } as any}
											/>
										)}
										<p className={`refund-amount ${isSelected ? 'selected' : 'unselected'}`}>
											{formatCurrency(refundAmount)}
										</p>
									</div>
								</div>
								<If condition={expanded}>
									<div className="bottom-content">
										<div className="line-item">
											<p>Booking price</p>
											<p>{formatCurrency(booking.maximumRefund)}</p>
										</div>
										<If condition={!(matchedCutoff?.staticFee ?? false)}>
											<div className="line-item">
												<p>Cancellation fee</p>
												<p>-{formatCurrency(booking.maximumRefund - refundAmount)}</p>
											</div>
										</If>
										<div className="line-item">
											<p
												className="wysiwyg-content"
												dangerouslySetInnerHTML={{ __html: cutoffRules[0] }}
											/>
											<p>&nbsp;</p>
										</div>
									</div>
								</If>
							</div>
						);
					})}
				</div>
			</div>
		);
	},
);
